// Import React
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Import Icons
import { IoAddCircleOutline } from 'react-icons/io5';

// Import Custom UI
import { DefaultText, PasswordText } from '../functions/TextModule'

// Import Custom Functions
import { encryptText, clearLocalStorage } from '../functions/LoginCheck';
import { appFunc, appConfig } from '../functions/FuncHeader';

/* =====================================
   D E F I N I T I O N
   ===================================== */

const userlistPropList = appFunc.appEnum.createEnum([
    "ulLoadingState",
    "isAddUserMode",
    "auDspTbValue",
    "auUidTbValue",
    "auPwdTbValue",
    "auCfmPwdTbValue",
    "isAuShowPwdTb",
    "isAuShowCfmPwdTb",
    "getAuEmptyUidMsg",
    "getAuEmptyPwdMsg",
    "isAuEmptyUid",
    "isAuEmptyPwd",
    "isAuEmptyCfmPwd",
    "getUserList",
    "getSelectedUser",
    "euDspTbValue",
    "euUidTbValue",
    "euPwdTbValue",
    "euCfmPwdTbValue",
    "isEuShowPwdTb",
    "isEuShowCfmPwdTb",
    "getEuEmptyUidMsg",
    "getEuEmptyPwdMsg",
    "isEuEmptyUid",
    "isEuEmptyPwd",
    "isEuEmptyCfmPwd",
    "getLastActivityList",
    "isReloadPage",
]);

const modeType = Object.freeze({
    invalid:    0,
    editUser:   1,
    removeUser: 2,
    lastMode:   3
});

const UserlistHeader = "UserListPage"

/* =====================================
   F U N C T I O N S
   ==================================== */

export default function UserList({getAppConfig, setAppConfig = () => {}})
{
    // Initialise
    const navigate = useNavigate();
    const [getUserListProp, setUserListProp] = useState({
        [userlistPropList.ulLoadingState]:      {},
        [userlistPropList.isAddUserMode]:       false,
        [userlistPropList.auDspTbValue]:        "",
        [userlistPropList.auUidTbValue]:        "",
        [userlistPropList.auPwdTbValue]:        "",
        [userlistPropList.auCfmPwdTbValue]:     "",
        [userlistPropList.isAuShowPwdTb]:       false,
        [userlistPropList.isAuShowCfmPwdTb]:    false,
        [userlistPropList.getAuEmptyUidMsg]:    "(Cannot be empty)",
        [userlistPropList.getAuEmptyPwdMsg]:    "(Cannot be empty)",
        [userlistPropList.isAuEmptyUid]:        false,
        [userlistPropList.isAuEmptyPwd]:        false,
        [userlistPropList.isAuEmptyCfmPwd]:     false,
        [userlistPropList.getUserList]:         [],
        [userlistPropList.getSelectedUser]:     {"UID": -1, "modeType": modeType.invalid},
        [userlistPropList.euDspTbValue]:        "",
        [userlistPropList.euUidTbValue]:        "",
        [userlistPropList.euPwdTbValue]:        "",
        [userlistPropList.euCfmPwdTbValue]:     "",
        [userlistPropList.isEuShowPwdTb]:       false,
        [userlistPropList.isEuShowCfmPwdTb]:    false,
        [userlistPropList.getEuEmptyPwdMsg]:    "(Cannot be empty)",
        [userlistPropList.isEuEmptyUid]:        false,
        [userlistPropList.isEuEmptyPwd]:        false,
        [userlistPropList.isEuEmptyCfmPwd]:     false,
        [userlistPropList.getLastActivityList]: [],
        [userlistPropList.isReloadPage]:        false,
    })

    /* Function: useEffect
       Info: Handle keydown event for creating user
       Returns: void
    */
    const handleKeyDown_createUser = (event) => 
    {
        if (event.key === "Enter")
        {
            ValidateCreateUser();
        }
        else
        {
            // Do Not Process
        }
    }

    /* Function: useEffect
       Info: Handle keydown event for saving modified user info
       Returns: void
    */
    const handleKeyDown_saveUserInfo = (event, username) => 
    {
        if (event.key === "Enter")
        {
            queryApiSaveUserInfo(username);
        }
        else
        {
            // Do Not Process
        }
    }

    /* Function: useEffect
       Info: To run on page load once, navigate to home if user is not an admin
       Returns: void
    */
    useEffect(() => {
        window.scrollTo(0, 0);
        if (getAppConfig[appConfig.isUserAdmin] === false)
        {
            navigate("/", { replace: true });
        }
        else
        {
            // DO NOTHING
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, getAppConfig[appConfig.isUserAdmin]])

    /* Function: useEffect
       Info: Function to load all data on first page load or 
            when reload page's data when is triggered
       Returns: void
    */
    useEffect(() => {
        if (getAppConfig[appConfig.isUserLogin] === true)
        {
            getUserList();
            getLastActivity();
            appFunc.updateUseState.object(userlistPropList.isReloadPage, false, setUserListProp);
        }
        else
        {
            // DO NOTHING
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAppConfig[appConfig.isUserLogin], getUserListProp[userlistPropList.isReloadPage]])

    /* Function: useEffect
       Info: Update loading state for loading UI to be displayed.
             loading state is updated false if all keys in hpLoadingState is false
       Returns: void
    */
    useEffect(() => {
            let isLoading = false;
            for (let state in getUserListProp[userlistPropList.ulLoadingState])
            {
                if (getUserListProp[userlistPropList.ulLoadingState][state] === true) 
                {
                    isLoading = state;
                    break;
                }
                else
                {
                    // Continue Searching
                }
            }
            appFunc.updateUseState.object(appConfig.isLoadingScreen, isLoading, setAppConfig);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [getUserListProp[userlistPropList.ulLoadingState]])

    /* Function: ValidateCreateUser()
       Info: Validate fields for user creation. Set error message when fields are invalid,
            and trigger user creation only when all fields are appropriate
       Returns: void
    */
    function ValidateCreateUser()
    {
        // Initialise
        let txtDsp = getUserListProp[userlistPropList.auDspTbValue];
        let txtUid = getUserListProp[userlistPropList.auUidTbValue];
        let txtPwd = getUserListProp[userlistPropList.auPwdTbValue];
        let txtCfmPwd = getUserListProp[userlistPropList.auCfmPwdTbValue];
        let pwd_valid = (txtPwd === txtCfmPwd);
        appFunc.updateUseState.object(userlistPropList.getAuEmptyUidMsg, "(Cannot be empty)", setUserListProp);
        appFunc.updateUseState.object(userlistPropList.getAuEmptyPwdMsg, "(Cannot be empty)", setUserListProp);
        appFunc.updateUseState.object(userlistPropList.isAuEmptyUid, txtUid === "", setUserListProp);
        appFunc.updateUseState.object(userlistPropList.isAuEmptyPwd, txtPwd === "", setUserListProp);
        appFunc.updateUseState.object(userlistPropList.isAuEmptyCfmPwd, txtCfmPwd === "", setUserListProp);
        if (txtUid === "" || txtPwd === "" || txtCfmPwd === "")
        {
            // Do Nothing
        }
        else if (pwd_valid === false) 
        {
            appFunc.updateUseState.object(userlistPropList.getAuEmptyPwdMsg, "(Confirm password do not match)", setUserListProp);
        }
        else if (txtPwd.length < 8)
        {
            appFunc.updateUseState.object(userlistPropList.getAuEmptyPwdMsg, "(Must be at least 8 character)", setUserListProp);
        }
        else if (txtUid !== "" && txtPwd !== "" && pwd_valid)
        {
            queryApiCreateUser(txtUid, txtPwd, txtDsp);
        }
        else
        {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                "[ValidateCreateUser] Inputs are invalid.");
        }
    }

    /* Function: queryApiCreateUser(txtUid, txtPwd, txtDsp) 
       Info: query to server to create a new user
       Returns: void
    */
    function queryApiCreateUser(txtUid, txtPwd, txtDsp) 
    {
        appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiCreateUser"], true, setUserListProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        fetch( process.env.REACT_APP_API_BASE_URL + "/users/sign-up", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            body: JSON.stringify({
                "displayname": txtDsp,
                "username": txtUid,
                "password": encryptText(txtPwd)
            })
        })
        .then(response => response.json())
        .then(response => {
            if (response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else if (response["detail"]["Error message"]["errno"] === 1062) 
                {
                    appFunc.updateUseState.object(userlistPropList.getAuEmptyUidMsg, "(Username taken)", setUserListProp);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                        "[queryApiCreateUser] Failed to create user.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                    `[queryApiCreateUser] ${response["Error"]}`);
            }
            else if (response["Success"] !== undefined) 
            {
                appFunc.updateUseState.object(userlistPropList.getAuEmptyUidMsg, "(Account Created)", setUserListProp);
                appFunc.updateUseState.object(userlistPropList.getAuEmptyPwdMsg, "(Cannot be empty)", setUserListProp);
                appFunc.updateUseState.object(userlistPropList.isReloadPage, true, setUserListProp);
                appFunc.updateUseState.object(userlistPropList.isAddUserMode, false, setUserListProp);
                appFunc.updateUseState.object(userlistPropList.auDspTbValue, "", setUserListProp);
                appFunc.updateUseState.object(userlistPropList.auUidTbValue, "", setUserListProp);
                appFunc.updateUseState.object(userlistPropList.auPwdTbValue, "", setUserListProp);
                appFunc.updateUseState.object(userlistPropList.auCfmPwdTbValue, "", setUserListProp);
            }
            else
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                    "[queryApiCreateUser] Unknown response.");
            }
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiCreateUser"], false, setUserListProp);
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                `[queryApiCreateUser] ${error}`);
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiCreateUser"], false, setUserListProp);
        });
    };

    /* Function: queryApiSaveUserInfo(selectedUsername)
       Info: query to API server to save user info for the selected user
       Returns: void
    */
    function queryApiSaveUserInfo(selectedUsername)
    {
        appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiSaveUserInfo"], true, setUserListProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        let txtDsp = getUserListProp[userlistPropList.euDspTbValue];
        let txtPwd = getUserListProp[userlistPropList.euPwdTbValue];
        let txtCfmPwd = getUserListProp[userlistPropList.euCfmPwdTbValue];
        let pwd_valid = txtPwd === txtCfmPwd;
        appFunc.updateUseState.object(userlistPropList.isEuEmptyPwd, txtPwd === "", setUserListProp);
        appFunc.updateUseState.object(userlistPropList.isEuEmptyCfmPwd, txtCfmPwd === "", setUserListProp);
        if (pwd_valid === false)
        {
            console.log("IN here")
            appFunc.updateUseState.object(userlistPropList.getEuEmptyPwdMsg, "(Password do not match)", setUserListProp);
        }
        else if (txtPwd.length < 8)
        {
            appFunc.updateUseState.object(userlistPropList.getEuEmptyPwdMsg, "(Must be at least 8 character)", setUserListProp);
        }
        else 
        {
            appFunc.updateUseState.object(userlistPropList.getEuEmptyPwdMsg, "(Cannot be empty)", setUserListProp);
            if (txtPwd !== "" && txtCfmPwd !== "" && pwd_valid)
            {
                fetch( process.env.REACT_APP_API_BASE_URL + "/user-list/update-userinfo", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + token
                    },
                    body: JSON.stringify({
                        "displayname": txtDsp,
                        "username": selectedUsername,
                        "newpassword": encryptText(txtPwd)
                    })
                })
                .then(response => response.json())
                .then(response => {
                    if (response["detail"] !== undefined)
                    {
                        if (response["detail"] === "Could not validate credentials")
                        {
                            clearLocalStorage(setAppConfig);
                        }
                        else
                        {
                            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                                "[ValidateCreateUser] Failed to save user's info.");
                        }
                    }
                    else if (response["Error"] !== undefined)
                    {
                        appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                            `[ValidateCreateUser] ${response["Error"]}`);
                    }
                    else if (response["Success"] !== undefined)
                    {
                        appFunc.updateUseState.object(userlistPropList.getEuEmptyUidMsg, "(Saved)", setUserListProp);
                        appFunc.updateUseState.object(userlistPropList.euPwdTbValue, "", setUserListProp);
                        appFunc.updateUseState.object(userlistPropList.euCfmPwdTbValue, "", setUserListProp);
                        appFunc.updateUseState.object(userlistPropList.getSelectedUser, {"UID": -1, "modeType": modeType.invalid}, setUserListProp);
                    }
                    else
                    {
                        appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                            "[ValidateCreateUser] Unknown response.");
                    }
                    appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiSaveUserInfo"], false, setUserListProp);
                })
                .catch(error => {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                        `[ValidateCreateUser] ${error}`);
                    appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiSaveUserInfo"], false, setUserListProp);
                });
            }
            else
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                    "[ValidateCreateUser] Inputs are invalid.");
                appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiSaveUserInfo"], false, setUserListProp);
            }
        }
    }

    /* Function: getUserList()
       Info: query to API server to get a list of users in dashboard api
       Returns: void
    */
    function getUserList() 
    {
        appFunc.updateUseState.object([userlistPropList.ulLoadingState, "getUserList"], true, setUserListProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        fetch( process.env.REACT_APP_API_BASE_URL + "/user-list/get-users", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            }
        })
        .then(response => response.json())
        .then(response => {
            if (response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                        "[getUserList] Failed to get user lists.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                    `[getUserList] ${response["Error"]}`);
            }
            else if (response["Success"] !== undefined)
            {
                appFunc.updateUseState.object(userlistPropList.getUserList, response["Success"], setUserListProp);
                // setUserList(response["Success"]);
                // setUserSelected(Array(response["Success"].length).fill(false));
            }
            else
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                    "[getUserList] Unknown response.");
            }
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "getUserList"], false, setUserListProp);
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                        `[getUserList] ${error}`);
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "getUserList"], false, setUserListProp);
        });
    };

    /* Function: queryApiSelectedUserInfo(userID)
       Info: query to API server to get the selected user's info
       Returns: void
    */
    function queryApiSelectedUserInfo(userID)
    {
        appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiSelectedUserInfo"], true, setUserListProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        fetch( process.env.REACT_APP_API_BASE_URL + "/user-list/get-selected-user", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            body: JSON.stringify({
                "username": userID
            })
        })
        .then(response => response.json())
        .then(response => {
            if (response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                        "[queryApiSelectedUserInfo] Failed to get selected user's info.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                    `[queryApiSelectedUserInfo] ${response["Error"]}`);
            }
            else if (response["Success"] !== undefined)
            {
                appFunc.updateUseState.object(userlistPropList.euDspTbValue, response["Success"], setUserListProp);
            }
            else
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                    "[queryApiSelectedUserInfo] Unknown response.");
            }
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiSelectedUserInfo"], false, setUserListProp);
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                        `[queryApiSelectedUserInfo] ${error}`);
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "queryApiSelectedUserInfo"], false, setUserListProp);
        });
    }

    /* Function: removeUser(userID)
       Info: query to API server to remove the selected user.
       Returns: void
    */
    function removeUser(userID)
    {
        appFunc.updateUseState.object([userlistPropList.ulLoadingState, "removeUser"], true, setUserListProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        fetch( process.env.REACT_APP_API_BASE_URL + "/user-list/remove-user", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            body: JSON.stringify({
                "username": userID
            })
        })
        .then(response => response.json())
        .then(response => {
            if (response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                        "[removeUser] Failed to remove user.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                    `[removeUser] ${response["Error"]}`);
            }
            else if (response["Success"] !== undefined)
            {
                appFunc.updateUseState.object(userlistPropList.getSelectedUser, {"UID": -1, "modeType": modeType.invalid}, setUserListProp);
                appFunc.updateUseState.object(userlistPropList.isReloadPage, true, setUserListProp);
            }
            else
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                    "[removeUser] Unknown response.");
            }
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "removeUser"], false, setUserListProp);
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                        `[removeUser] ${error}`);
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "removeUser"], false, setUserListProp);
        });
    }

    /* Function: getLastActivity()
       Info: query to API server to get a list of last login to dashboard api
       Returns: void
    */
    function getLastActivity()
    {
        appFunc.updateUseState.object([userlistPropList.ulLoadingState, "getLastActivity"], true, setUserListProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        fetch( process.env.REACT_APP_API_BASE_URL + "/user-list/get-last-activities", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            }
        })
        .then(response => response.json())
        .then(response => {
            if(response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                        "[getLastActivity] Failed to get last activities.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                    `[getLastActivity] ${response["Error"]}`);
            }
            else if (response["Success"] !== undefined)
            {
                appFunc.updateUseState.object(userlistPropList.getLastActivityList, response["Success"], setUserListProp);
            }
            else
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader, 
                    "[getLastActivity] Unknown response.");
            }
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "getLastActivity"], false, setUserListProp);
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                `[getLastActivity] ${error}`);
            appFunc.updateUseState.object([userlistPropList.ulLoadingState, "getLastActivity"], false, setUserListProp);
        });
    }

    /* Function: convertDateTime(date)
       Info: build date time to ddMM
       Returns: void
    */
    function convertDateTime(date)
    {
        let dateObj = new Date(date);
        let formatActivity = dateObj.toLocaleDateString('en-GB', {day: '2-digit', month: 'short'});
        return formatActivity;
    }

    /* Function: getUserList()
       Info: build date time to ddMMYY, HH,mm,ss
       Returns: void
    */
    function convertDateTime2(date) {
        let dateObj = new Date(date);
        let formatActivity = dateObj.toLocaleString('en-GB', {
            day: '2-digit',    // dd
            month: 'short',    // MMM
            year: '2-digit',   // yy
            hour: '2-digit',   // HH
            minute: '2-digit', // mm
            second: '2-digit', // mm
            hour12: false      // 24-hour format
        });
        
        let [datePart, timePart] = formatActivity.split(', ');
        formatActivity = `SGT ${datePart}, ${timePart}`;
        
        return formatActivity;
    }

    return (
        <div className="UserProfile">
            {
                getUserListProp[userlistPropList.getSelectedUser]["modeType"] === modeType.removeUser && (
                    <div className="DeletePrompt-MainContainer">
                        <div className="DeletePrompt-InnerContainer">
                            <p className="DeletePrompt-Text">Are you sure you want to remove user "{getUserListProp[userlistPropList.euDspTbValue]}"?</p>
                            <div className="DeletePrompt-ButtonContainer">
                                <button className="DeletePrompt-Button accept" onClick={() => {
                                    if (getUserListProp[userlistPropList.getSelectedUser]["UID"] !== -1)
                                    {
                                        removeUser(getUserListProp[userlistPropList.getSelectedUser]["UID"]);
                                        appFunc.updateUseState.object([userlistPropList.getSelectedUser], {"UID": -1, "modeType": modeType.invalid}, setUserListProp);
                                    }
                                }}>Yes</button>
                                <button className="DeletePrompt-Button decline" onClick={() => {
                                    if (getUserListProp[userlistPropList.getSelectedUser]["UID"] !== -1)
                                    {
                                        appFunc.updateUseState.object([userlistPropList.getSelectedUser, "modeType"], modeType.invalid, setUserListProp);
                                    }
                                    else
                                    {
                                        appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, UserlistHeader,
                                            `[Remove_User_Prompt] Invalid behaviour to have an unknown selected user ID`);
                                    }
                                }}>No</button>
                            </div>
                        </div>
                    </div>
                )
            }
            <div className="UserProfile-NewUser">
                <div className='UserProfile-AddUserBtn-Container'>
                    <button className="UserProfile-AddUserBtn" onClick={
                        () => {
                            appFunc.updateUseState.object(userlistPropList.getAuEmptyUidMsg, "(Cannot be empty)", setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.getAuEmptyPwdMsg, "(Cannot be empty)", setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.isAuEmptyUid, false, setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.isAuEmptyPwd, false, setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.isAuEmptyCfmPwd, false, setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.isAuShowPwdTb, false, setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.isAuShowCfmPwdTb, false, setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.auDspTbValue, "", setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.auUidTbValue, "", setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.auPwdTbValue, "", setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.auCfmPwdTbValue, "", setUserListProp);
                            appFunc.updateUseState.object(userlistPropList.isAddUserMode, !getUserListProp[userlistPropList.isAddUserMode], setUserListProp);
                        }}>
                        <IoAddCircleOutline />
                        Add User
                    </button>
                    <button className={`loginSubmit ${getUserListProp[userlistPropList.isAddUserMode] ? '' : 'hide'}`} 
                        disabled={!getUserListProp[userlistPropList.isAddUserMode]} 
                        onClick={() => {
                            ValidateCreateUser();
                        }}>
                        Create User
                    </button>
                </div>
                <div className={`UserProfile-AddUserContainer ${getUserListProp[userlistPropList.isAddUserMode] ? '' : 'hide'}`}>
                    <DefaultText
                        txtLabel="Display Name"
                        changeTask={event => appFunc.updateUseState.object(userlistPropList.auDspTbValue, event.target.value, setUserListProp)}
                        keyDownTask={handleKeyDown_createUser}
                        inputID="cuDspTxt"
                        inputDisabled={getUserListProp[userlistPropList.isAddUserMode] === false}
                        inputMaxLen={50}
                        inputValue={getUserListProp[userlistPropList.auDspTbValue]}
                    />
                    <DefaultText
                        txtLabel="Username"
                        txtErrorLabel={getUserListProp[userlistPropList.getAuEmptyUidMsg]}
                        txtErrorToggle={getUserListProp[userlistPropList.isAuEmptyUid] || getUserListProp[userlistPropList.getAuEmptyUidMsg] !== "(Cannot be empty)"}
                        changeTask={event => appFunc.updateUseState.object(userlistPropList.auUidTbValue, event.target.value, setUserListProp)}
                        keyDownTask={handleKeyDown_createUser}
                        inputID="cuUidTxt"
                        inputDisabled={getUserListProp[userlistPropList.isAddUserMode] === false}
                        inputValue={getUserListProp[userlistPropList.auUidTbValue]}
                    />
                    <PasswordText
                        txtLabel="Password"
                        txtErrorLabel={getUserListProp[userlistPropList.getAuEmptyPwdMsg]}
                        txtErrorToggle={getUserListProp[userlistPropList.isAuEmptyPwd] || getUserListProp[userlistPropList.getAuEmptyPwdMsg] !== "(Cannot be empty)"}
                        changeTask={event => appFunc.updateUseState.object(userlistPropList.auPwdTbValue, event.target.value, setUserListProp)}
                        keyDownTask={handleKeyDown_createUser}
                        inputID="cuPwdTxt"
                        inputDisabled={getUserListProp[userlistPropList.isAddUserMode] === false}
                        inputValue={getUserListProp[userlistPropList.auPwdTbValue]}
                        showPwd={getUserListProp[userlistPropList.isAuShowPwdTb]}
                        showPwdEvent={() => appFunc.updateUseState.object(userlistPropList.isAuShowPwdTb, !getUserListProp[userlistPropList.isAuShowPwdTb], setUserListProp)}
                    />
                    <PasswordText
                        txtLabel="Confirm Password"
                        txtErrorToggle={getUserListProp[userlistPropList.isAuEmptyCfmPwd]}
                        changeTask={event => appFunc.updateUseState.object(userlistPropList.auCfmPwdTbValue, event.target.value, setUserListProp)}
                        keyDownTask={handleKeyDown_createUser}
                        inputID="cuCfmPwdTxt"
                        inputDisabled={getUserListProp[userlistPropList.isAddUserMode] === false}
                        inputValue={getUserListProp[userlistPropList.auCfmPwdTbValue]}
                        showPwd={getUserListProp[userlistPropList.isAuShowCfmPwdTb]}
                        showPwdEvent={() => appFunc.updateUseState.object(userlistPropList.isAuShowCfmPwdTb, !getUserListProp[userlistPropList.isAuShowCfmPwdTb], setUserListProp)}
                    />
                </div>
            </div>
            <div className="UserProfile-UserList-Container">
                <div className="UserProfile-UserList-Title-Container">
                    <div className="UserProfile-UserList-Title">Users</div>
                </div>
                <div className="UserProfile-UserList-User-Container">
                {
                    getUserListProp[userlistPropList.getUserList].length === 0 ? "" :
                    getUserListProp[userlistPropList.getUserList].map((data, index) => {
                        return (
                            <div key={index} className={`UserProfile-UserList-UserIndiv-Container ${data["username"] === getUserListProp[userlistPropList.getSelectedUser]["UID"] ? 'highlighted' : ''}`} 
                                onClick={() => {
                                    if (( getUserListProp[userlistPropList.getSelectedUser]["UID"] === -1 && getUserListProp[userlistPropList.getSelectedUser]["modeType"] === modeType.invalid ) ||
                                        getUserListProp[userlistPropList.getSelectedUser]["UID"] !== data["username"])
                                    {
                                        appFunc.updateUseState.object(userlistPropList.getSelectedUser, {"UID": data["username"], "modeType": modeType.editUser}, setUserListProp);
                                        appFunc.updateUseState.object(userlistPropList.euPwdTbValue, "", setUserListProp);
                                        appFunc.updateUseState.object(userlistPropList.euCfmPwdTbValue, "", setUserListProp);
                                        appFunc.updateUseState.object(userlistPropList.isEuShowPwdTb, false, setUserListProp);
                                        appFunc.updateUseState.object(userlistPropList.isEuShowCfmPwdTb, false, setUserListProp);
                                        queryApiSelectedUserInfo(data["username"]);
                                    }
                                    else
                                    {
                                        appFunc.updateUseState.object(userlistPropList.getSelectedUser, {"UID": -1, "modeType": modeType.invalid}, setUserListProp);
                                    }
                                }
                            }>
                                <div className="UserProfile-UserList-UserIndiv-Name">{data["username"]}</div>
                                <div className="UserProfile-UserList-UserIndiv-Activities">Last Activity: {data["last_activity"] === null ? "NEW" : convertDateTime(data["last_activity"])}</div>
                            </div>
                        )
                    })
                }
                </div>
                <div className={`UserProfile-Config-Container ${getUserListProp[userlistPropList.getSelectedUser]["UID"] === -1 ? 'hide' : ''}`}>
                    <div className="UserProfile-Config-Title-Container">
                        <div className="UserProfile-Config-Title">Change details: </div>
                        <div className="UserProfile-Config-Title-UID">{getUserListProp[userlistPropList.getSelectedUser]["UID"]}</div>
                    </div>
                    <div className="UserProfile-Config-Container-Inner">
                        <DefaultText
                            txtLabel="Display Name"
                            changeTask={event => appFunc.updateUseState.object(userlistPropList.euDspTbValue, event.target.value, setUserListProp)}
                            keyDownTask={(event) => handleKeyDown_saveUserInfo(event, getUserListProp[userlistPropList.getSelectedUser]["UID"])}
                            inputID="euDspTxt"
                            inputDisabled={getUserListProp[userlistPropList.getSelectedUser]["UID"] === -1}
                            inputMaxLen={50}
                            inputValue={getUserListProp[userlistPropList.euDspTbValue]}
                        />
                        <PasswordText
                            txtLabel="New Password"
                            txtErrorLabel={getUserListProp[userlistPropList.getEuEmptyPwdMsg]}
                            txtErrorToggle={getUserListProp[userlistPropList.isEuEmptyCfmPwd] || getUserListProp[userlistPropList.getEuEmptyPwdMsg] !== "(Cannot be empty)"}
                            changeTask={event => appFunc.updateUseState.object(userlistPropList.euPwdTbValue, event.target.value, setUserListProp)}
                            keyDownTask={(event) => handleKeyDown_saveUserInfo(event, getUserListProp[userlistPropList.getSelectedUser]["UID"])}
                            inputID="euPwdTxt"
                            inputDisabled={getUserListProp[userlistPropList.getSelectedUser]["UID"] === -1}
                            inputValue={getUserListProp[userlistPropList.euPwdTbValue]}
                            showPwd={getUserListProp[userlistPropList.isEuShowPwdTb]}
                            showPwdEvent={() => appFunc.updateUseState.object(userlistPropList.isEuShowPwdTb, !getUserListProp[userlistPropList.isEuShowPwdTb], setUserListProp)}
                            
                        />
                        <PasswordText
                            txtLabel="Confirm Password"
                            txtErrorToggle={getUserListProp[userlistPropList.isEuEmptyCfmPwd]}
                            changeTask={event => appFunc.updateUseState.object(userlistPropList.euCfmPwdTbValue, event.target.value, setUserListProp)}
                            keyDownTask={(event) => handleKeyDown_saveUserInfo(event, getUserListProp[userlistPropList.getSelectedUser]["UID"])}
                            inputID="euCfmPwdTxt"
                            inputDisabled={getUserListProp[userlistPropList.getSelectedUser]["UID"] === -1}
                            inputValue={getUserListProp[userlistPropList.euCfmPwdTbValue]}
                            showPwd={getUserListProp[userlistPropList.isEuShowCfmPwdTb]}
                            showPwdEvent={() => appFunc.updateUseState.object(userlistPropList.isEuShowCfmPwdTb, !getUserListProp[userlistPropList.isEuShowCfmPwdTb], setUserListProp)}
                        />
                    </div>
                    <div className="UserProfile-Button-Container">
                        <div className="UserProfile-DeleteButton"
                            disabled={getUserListProp[userlistPropList.getSelectedUser]["UID"] === -1}
                            onClick={() => {
                                appFunc.updateUseState.object([userlistPropList.getSelectedUser, "modeType"], modeType.removeUser, setUserListProp);
                            }}>
                            Remove
                        </div>
                        <div className="UserProfile-SaveButton"
                            disabled={getUserListProp[userlistPropList.getSelectedUser]["UID"] === -1}
                            onClick={() => queryApiSaveUserInfo(getUserListProp[userlistPropList.getSelectedUser]["UID"])}>
                            Save
                        </div>
                    </div>
                </div>
            </div>
            <div className="UserProfile-LastActivity-Container">
                <div className="UserProfile-LastActivity-Title-Container">
                    <div className="UserProfile-LastActivity-Title">Last Activity</div>
                </div>
                <div className="UserProfile-LastActivity-Activity-Container">
                {
                    getUserListProp[userlistPropList.getLastActivityList].length === 0 ? "" :
                    getUserListProp[userlistPropList.getLastActivityList].map((data, index) => {
                        return (
                            <div key={index} className="UserProfile-LastActivity-Activity-Indiv-Container">
                                <div className="UserProfile-LastActivity-Activity-Indiv-Username">{data["username"]}</div>
                                <div className="UserProfile-LastActivity-Activity-Indiv-IP">{data["ip_address"]}</div>
                                <div className="UserProfile-LastActivity-Activity-Indiv-Content">{convertDateTime2(data["last_activity"])}</div>
                            </div>
                        )
                    })
                }
                </div>
            </div>
        </div>
    );
}